import { api } from '../api';

export interface SearchMatchContext {
  primary_match_type: string;
  all_match_types: string[];
  confidence: 'high' | 'medium' | 'low';
}

export interface SearchResult {
  id: string | number; // Support both UUID strings and integer IDs
  name: string;
  source_table: string;
  project_type: string;
  match_context: SearchMatchContext;
  search_score: number;
  highlight_info: Record<string, string[]>;
  description?: string;
  methodology?: string;
  status?: string;
  registry?: string;
  country?: string;
  adm1?: string;
  adm2?: string;
  proponent_name?: string;
  validator_name?: string;
  area_in_hector?: number;
  suitability_score?: number;
  area?: number;
}

export interface SearchResponse {
  suggestions: SearchResult[];
  total_found: number;
  search_info?: {
    query: string;
    source: string;
    search_time_ms?: number;
  };
}

export interface SearchSuggestionsResponse {
  suggestions: string[];
}

/**
 * Search service for Typesense-powered global search
 */
export class SearchService {
  /**
   * Performs a unified search across all project types
   */
  static async search(
    query: string,
    limit: number = 10,
    offset: number = 0,
  ): Promise<SearchResponse> {
    if (!query || query.trim().length === 0) {
      return {
        suggestions: [],
        total_found: 0,
      };
    }

    try {
      const response = await api.get<SearchResponse>(`/search/${encodeURIComponent(query)}`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      console.error('Search error:', error);
      return {
        suggestions: [],
        total_found: 0,
      };
    }
  }

  /**
   * Gets search suggestions for autocomplete
   */
  static async getSuggestions(query: string, limit: number = 5): Promise<string[]> {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      const response = await api.get<SearchSuggestionsResponse>(
        `/search/suggestions/${encodeURIComponent(query)}`,
        {
          params: { limit },
        },
      );
      return response.data.suggestions;
    } catch (error) {
      console.error('Search suggestions error:', error);
      return [];
    }
  }
}
